import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export const Menu = ({ isMenuOpen, closeMenu }) => {
  const [machines, setMachines] = useState([]);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const fetchParsedData = () => {
    const storedData = localStorage.getItem('liveData');
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData); // Fixed here: no need to decodeURIComponent
        const machineEntries = Object.values(parsedData).map((entry) => {
          const { metadata, liveData } = entry;

          // Calculate average SoC
          const socValues = [
            liveData.Soc1,
            liveData.Soc2,
            liveData.Soc3, // Accommodates future SoC3
          ].filter((soc) => soc !== undefined);

          const averageSoc =
            socValues.reduce((sum, soc) => sum + soc, 0) / socValues.length || 0;

          return {
            metadata,
            liveData,
            averageSoc: Math.round(averageSoc), // Rounded for cleaner display
          };
        });

        // Sort the machines: Running > Charging > Alphabetical
        const sortedMachines = machineEntries.sort((a, b) => {
          const isRunningA = a.liveData.MotorSpeed > 150;
          const isRunningB = b.liveData.MotorSpeed > 150;

          if (isRunningA !== isRunningB) {
            return isRunningB - isRunningA; // Running first
          }

          const isChargingA = Object.keys(a.liveData)
          .filter(key => key.startsWith("IsCharging")).some((key) => a.liveData[key] >= 1);
          const isChargingB = Object.keys(b.liveData)
          .filter(key => key.startsWith("IsCharging")).some((key) => b.liveData[key] >= 1);

          if (isChargingA !== isChargingB) {
            return isChargingB - isChargingA; // Charging second
          }

          // Alphabetical by brand + model
          const nameA = `${a.metadata.brand} ${a.metadata.model}`.toLowerCase();
          const nameB = `${b.metadata.brand} ${b.metadata.model}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });

        setMachines(sortedMachines);
      } catch (error) {
        console.error('Failed to parse liveData from localStorage:', error);
      }
    }
  };

  useEffect(() => {
    // Fetch data every 10 seconds
    const intervalId = setInterval(() => {
      fetchParsedData();
    }, 10000);

    return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, []);

  useEffect(() => {
    // Fetch data when menu opens
    if (isMenuOpen) {
      fetchParsedData();
    }
  }, [isMenuOpen]);

  useEffect(() => {
    // Close the menu when clicking outside
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, closeMenu]);

  const handleMachineClick = (machine) => {
    closeMenu(); // Close the menu
    navigate(`/machine/${machine.metadata.serialNumber}`); // Navigate to machine dashboard
  };

  return (
    <div
      ref={menuRef}
      className={`fixed top-16 right-4 bg-white shadow-lg rounded-md w-128 p-4 transform transition-transform ${
        isMenuOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0 pointer-events-none'
      }`}
    >
      <ul className="space-y-4">
        {machines.map((machine, index) => {
          const { alias, brand, model } = machine.metadata;
          const { MotorSpeed } = machine.liveData;
          const IsCharging = Object.keys(machine.liveData)
          .filter(key => key.startsWith("IsCharging"))
          .some((key) => machine.liveData[key] >= 1);

          return (
            <li
              key={index}
              className="cursor-pointer p-4 border-b border-gray-200 last:border-none text-gray-700 hover:text-orange-500 rounded-md flex justify-between items-center"
              onClick={() => handleMachineClick(machine)}
            >
              {/* Machine Details */}
              <div className="mr-4"> {/* Add margin to the right */}
                <div className="text-sm font-medium">
                  {brand} {model}
                </div>
                <div className="text-xs text-gray-500 mt-1">{alias}</div>
              </div>

              {/* Average Battery Percentage and Status Dot */}
              <div className="flex items-center">
                <span className="text-xs font-medium text-gray-700 mr-2">
                  {machine.averageSoc}%
                </span>
                <div
                  className={`w-3 h-3 rounded-full ${
                    IsCharging
                      ? 'bg-yellow-500'
                      : MotorSpeed > 150
                      ? 'bg-green-500'
                      : 'bg-gray-400'
                  }`}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
